import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { FormConditionalLogic } from '../Models/ConditionalLogic-data.model';
import { SectionInfo } from '../Models/Question-details';
import { WINDOW } from '../constants/window';
import { getTenant } from '../shared/tenant.util';

@Injectable({
  providedIn: 'root',
})
export class ApiServiceService {
  tenant = getTenant();
  // SM-3347: Removed FormsAPIBaseUrl, FormsAPIToken, FormAPIAppID - no longer needed
  // All API calls route through OBM-Core which handles Form Services authentication
  endPointObj = this.loadConfig('assets/urls.config.json', 'endPoint')

  features = this.loadConfig('assets/features.config.json', 'features');


  // SM-3454: `dynamicEndPoint` must NOT be cached at construction. This service
  // is a `providedIn: 'root'` singleton, so a cached value would freeze whatever
  // `mode` happened to be in localStorage at construction time. Because
  // AppComponent clears `mode` (SM-3429) and only writes it back after decoding
  // the JWT, a Supplier session could construct this service while `mode` was
  // still empty and permanently route every call to the Buyer endpoint (403).
  // Reading localStorage on each access makes the endpoint reflect live state.
  get mode(): string {
    return localStorage.getItem('mode') ?? '';
  }

  get dynamicEndPoint(): string {
    return `${this.tenant ?? ''}` +
      `${this.mode === 'supplierEdit'
        ? this.endPointObj['supplierPreview']
        : this.endPointObj['buyerPreview']}`;
  }

  constructor(
    private httpClient: HttpClient,
    @Inject(WINDOW) private _window: Window,
  ) {}

  toPascalCase(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  /**
   * Recursively converts all object keys to PascalCase.
   * This handles the difference between .NET Core (camelCase) and .NET Framework (PascalCase) API responses.
   * Converts keys like: uniqueID -> UniqueID, title -> Title, etc.
   * Use this for section data, template data, etc.
   */
  public convertKeysToPascalCase(obj: any): any {
    if (obj === null || typeof obj !== 'object') {
      return obj; // Return as-is for null and primitives
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.convertKeysToPascalCase(item));
    }
    
    // Recursively convert all keys to PascalCase
    return Object.keys(obj).reduce((result: any, key: string) => {
      const pascalKey = this.toPascalCase(key);
      result[pascalKey] = this.convertKeysToPascalCase(obj[key]);
      return result;
    }, {});
  }

  /**
   * Converts only the top-level response wrapper keys to PascalCase.
   * Does NOT recursively convert nested object keys to preserve the original data structure.
   * Use this for conditional logic JSON where nested properties must remain lowercase.
   */
  public convertTopLevelKeysToPascalCase(obj: any): any {
    if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) {
      return obj; // Return as-is for null, primitives, and arrays
    }
    
    // Only convert top-level wrapper keys, preserve nested data as-is
    return Object.keys(obj).reduce((result: any, key: string) => {
      const pascalKey = this.toPascalCase(key);
      result[pascalKey] = obj[key]; // Don't recursively convert
      return result;
    }, {});
  }

  public GetSectionDetails(Sectionid: any): Observable<any> {
    const apiUrl = `${this.dynamicEndPoint}/GetSectionDetails?id=`;
    return this.httpClient.get<any>(`${apiUrl}${Sectionid}?v=${new Date().toISOString()}`)
      .pipe(
        map(response => this.convertKeysToPascalCase(response))
      );
  }

  public GetSections(): Observable<any> {
    const apiUrl = `${this.dynamicEndPoint}/GetSections?id=`;
    let TemplateId = localStorage.getItem('TemplateId') || '';
    return this.httpClient.get<any>(`${apiUrl}${TemplateId}?v=${new Date().toISOString()}`)
      .pipe(
        map(response => this.convertKeysToPascalCase(response))
      );
  }


  public GetRegEx(): Observable<any> {
    const apiUrl = `${this.dynamicEndPoint.replace("FWClient", "Question")}/GetRegularExpressions`;
    return this.httpClient.get<any>(apiUrl)
      .pipe(
        map(response => this.convertKeysToPascalCase(response))
      );
  }


  /**
   * Upload a file for AI form generation. Returns the AnalyzeTemplateResult
   * ({ assessment, form }) verbatim — the `form` is already in ParseResult shape,
   * so it is intentionally NOT PascalCase-converted (the import flow consumes the
   * camelCase shape directly, like ExcelImportService output).
   */
  public AnalyzeTemplateAI(file: File): Observable<any> {
    const apiUrl = `${this.dynamicEndPoint}/AnalyzeTemplateAI`;
    const formData = new FormData();
    formData.append('file', file, file.name);
    // Scope standing-rule recall to this form (org-global rules still apply).
    formData.append('templateId', localStorage.getItem('TemplateId') || '');
    return this.httpClient.post<any>(apiUrl, formData);
  }

  /**
   * Apply a natural-language instruction to the current form. Body:
   * { currentForm, instruction, history }. Returns { updatedForm, summary } —
   * updatedForm is a ParseResult, so it is NOT PascalCase-converted.
   */
  public EditFormAI(payload: any): Observable<any> {
    const apiUrl = `${this.dynamicEndPoint}/EditFormAI`;
    return this.httpClient.post<any>(apiUrl, payload);
  }

  /**
   * Persist a standing AI rule for the current org (the "Save as a rule?" UX).
   * Applied to every future generate/edit as long-term memory.
   */
  public SaveFormAiRule(instruction: string, templateId?: string, isGlobal = false): Observable<any> {
    const apiUrl = `${this.dynamicEndPoint}/SaveFormAiRule`;
    return this.httpClient.post<any>(apiUrl, {
      instruction,
      // null/empty templateId on the server → org-global. When isGlobal we omit it.
      templateId: isGlobal ? '' : (templateId || localStorage.getItem('TemplateId') || ''),
      isGlobal,
    });
  }

  public SaveSection(section: SectionInfo | any): Observable<any> {
    const apiUrl = `${this.dynamicEndPoint}/SaveSection`;
    return this.httpClient.post<any>(
      `${apiUrl}`,
      section,
    ).pipe(
      map(response => this.convertKeysToPascalCase(response))
    );
  }

  /** Save attribute mappings (one unique AttributeName per mappable question). */
  public SaveAttributeDataMap(payload: any): Observable<any> {
    const apiUrl = `${this.dynamicEndPoint}/SaveAttributeDataMap`;
    return this.httpClient.post<any>(`${apiUrl}`, payload);
  }

  /** Fetch the saved attribute mappings for a form (used to auto-map only NEW questions). */
  public GetFormDataMappingByFormId(formId: string | number): Observable<any> {
    const apiUrl = `${this.dynamicEndPoint}/GetFormDataMappingByFormId`;
    return this.httpClient.get<any>(`${apiUrl}?baseFormID=${formId}`);
  }

  /**
   * Read a Settings-tab value by key (existing endpoint). Returns { success, data }.
   * Used to gate the AI Form Assistant (Phase 2) via `isAiFormAssistantEnabled`.
   */
  public GetSettingValue(settingKey: string): Observable<any> {
    const apiUrl = `${this.dynamicEndPoint}/GetSettingValue`;
    return this.httpClient.get<any>(`${apiUrl}?settingKey=${encodeURIComponent(settingKey)}`);
  }

  public SaveConditionalLogic(
    conditionalLogicData: FormConditionalLogic,
  ): Observable<any> {
    const apiUrl = `${this.dynamicEndPoint}/SaveConditionalLogic`;
    return this.httpClient.post<any>(
      `${apiUrl}`,
      conditionalLogicData,
    ).pipe(
      map(response => this.convertKeysToPascalCase(response))
    );
  }
  public GetConditionaDetails(): Observable<any> {
    const apiUrl = `${this.dynamicEndPoint}/GetConditionaDetails?id=`;
    let TemplateId = localStorage.getItem('TemplateId') || '';
    return this.httpClient.get<any>(`${apiUrl}${TemplateId}?v=${new Date().toISOString()}`)
      .pipe(
        map(response => this.convertKeysToPascalCase(response))
      );
  }

  public DeleteSection(Section: SectionInfo | any): Observable<any> {
    const apiUrl = `${this.dynamicEndPoint}/DeleteSection`;
    return this.httpClient.post<any>(
      `${apiUrl}`,
      Section,
    ).pipe(
      map(response => this.convertKeysToPascalCase(response))
    );
  }

  public GetQuestionsByTemplateID(): Observable<any> {
    const apiUrl = `${this.dynamicEndPoint}/GetQuestionsByTemplateID`;
    let TemplateId = localStorage.getItem('TemplateId') || '';
    return this.httpClient.get<any>(
      `${this._window.location.origin
      }/${apiUrl}/${TemplateId}?v=${new Date().toISOString()}`,
    ).pipe(
      map(response => this.convertKeysToPascalCase(response))
    );
  }

  /**
   * SM-3312: Fetch the Exostar portal logo as a Blob for display in the header.
   * Uses tenant base URL when available, falls back to window.location.origin.
   */
  public GetLogo(): Observable<Blob> {
    const baseUrl = this.tenant || this._window.location.origin;
    const apiUrl = `${baseUrl}/Shared/Common/GetLogo`;
    return this.httpClient.get(apiUrl, { responseType: 'blob' });
  }

  /**
   * SM-3312: Fetch the logged-in user's details (name, role, navigation URLs)
   * for display in the portal header.
   * Uses tenant base URL when available, falls back to window.location.origin.
   */
  public GetUserLoginDetails(): Observable<any> {
    const baseUrl = this.tenant || this._window.location.origin;
    const apiUrl = `${baseUrl}/Shared/Common/GetUserloginDetails`;
    return this.httpClient.get<any>(apiUrl);
  }

  loadConfig(file: any, property: any) {
    return this.loadJSON(file)[property];
  }
  loadJSON(filePath: any) {
    const json = this.loadTextFileAjaxSync(filePath, 'application/json') || '';
    return JSON.parse(json);
  }

  loadTextFileAjaxSync(filePath: string, mimeType: string | null) {
    const xmlhttp = new XMLHttpRequest();
    xmlhttp.open('GET', filePath, false);
    if (mimeType != null) {
      if (xmlhttp.overrideMimeType) {
        xmlhttp.overrideMimeType(mimeType);
      }
    }
    xmlhttp.send();
    if (xmlhttp.status == 200) {
      return xmlhttp.responseText;
    } else {
      return null;
    }
  }

}
